import { Snackbar, Alert } from "@mui/material";
import React, { useState } from "react";

import {
  NotificationContext,
  type NotificationState,
  type NotificationContextType,
} from "./NotificationContext.types";

import type { AlertColor } from "@mui/material";
import type { ReactNode } from "react";

// Экспорт типов и контекста для использования в других файлах
export { NotificationContext, type NotificationContextType };

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [notification, setNotification] = useState<
    NotificationState & { id: number }
  >({
    id: 0,
    open: false,
    message: "",
    severity: "info",
    autoHideDuration: 6000,
  });

  const showNotification = (
    message: string,
    severity: AlertColor = "info",
    autoHideDuration: number = 6000,
  ): void => {
    setNotification((previous) => ({
      id: previous.id + 1,
      open: true,
      message,
      severity,
      autoHideDuration,
    }));
  };

  const showError = (message: string): void => {
    showNotification(message, "error", 8000); // Longer duration for errors
  };

  const showSuccess = (message: string): void => {
    showNotification(message, "success", 4000);
  };

  const showWarning = (message: string): void => {
    showNotification(message, "warning", 6000);
  };

  const showInfo = (message: string): void => {
    showNotification(message, "info", 5000);
  };

  const hideNotification = (): void => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  return (
    <NotificationContext.Provider
      value={{
        showNotification,
        showError,
        showSuccess,
        showWarning,
        showInfo,
        hideNotification,
      }}
    >
      {children}
      <Snackbar
        key={notification.id}
        open={notification.open}
        autoHideDuration={notification.autoHideDuration}
        onClose={(_event, reason): void => {
          if (reason !== "clickaway") hideNotification();
        }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 8 }} // Add margin top to avoid AppBar overlap
      >
        <Alert
          onClose={hideNotification}
          severity={notification.severity}
          variant="filled"
          sx={{
            width: "100%",
            maxWidth: 400,
            alignItems: "center",
            "& .MuiAlert-icon": { py: 0, alignItems: "center" },
            "& .MuiAlert-action": { py: 0, alignItems: "center" },
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};
